/**
 * When `REACT_APP_BACKEND_URL` is unset, `api-urls` used to emit strings like
 * `undefined/auth/refresh-token`. Axios then resolves those as relative URLs, producing broken
 * paths such as `/projects/123/undefined/auth/refresh-token` on the backend.
 */
import { isBetterAuthEnabled } from "./better-auth.constants";
import { readPosthogReleaseVersion } from "./posthog-release";

const DEV_API_FALLBACK = "http://localhost:3000";

function readTurnstileSiteKeyFromEnv(): string {
  const canonical = (process.env.REACT_APP_TURNSTILE_SITE_KEY as string | undefined)?.trim() ?? "";
  if (canonical.length > 0) return canonical;
  return (
    (process.env.REACT_APP_SCRIPT_SHARE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ?? ""
  );
}

function envUrlWithDevFallback(raw: string | undefined, devFallback: string): string {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (trimmed.length > 0) return trimmed;
  return process.env.NODE_ENV === "production" ? "" : devFallback;
}

export const ENVS = {
  landingPageUrl: (process.env.REACT_APP_LANDING_PAGE_URL as string) || "https://rivetai.com",
  backendUrl: envUrlWithDevFallback(process.env.REACT_APP_BACKEND_URL, DEV_API_FALLBACK),
  posthogKey: process.env.REACT_APP_POSTHOG_KEY as string,
  posthogApiHost: process.env.REACT_APP_POSTHOG_API_HOST as string,
  posthogUiHost: process.env.REACT_APP_POSTHOG_UI_HOST as string,
  /** Deploy commit SHA; matches PostHog source-map `release-version`. Empty locally. */
  releaseVersion: readPosthogReleaseVersion(),
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET as string,
  firebaseProjectId: process.env.REACT_APP_PROJECT_ID as string,
  freePlanId: process.env.REACT_APP_FREE_PLAN_ID as string,
  starterPlanId: process.env.REACT_APP_STARTER_PLAN_ID as string,
  indiePlanId: process.env.REACT_APP_INDIE_PLAN_ID as string,
  googlePlacesApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY as string,
  onPrem: process.env.REACT_APP_ON_PREM === "true",
  /** Stripe.js publishable key (pk_…); required for Embedded Checkout. */
  stripePublishableKey: (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string) || "",
  otpEmbeddedCheckoutEnabled: process.env.REACT_APP_OTP_EMBEDDED_CHECKOUT === "true",
  /**
   * Cloudflare Turnstile site key (auth + `/s/:token` script share).
   * Set together with backend `TURNSTILE_SECRET_KEY` when enforcing verification.
   */
  turnstileSiteKey: readTurnstileSiteKeyFromEnv(),
  /** Optional “What’s new” / changelog URL shown on the app update banner */
  changelogUrl: (process.env.REACT_APP_CHANGELOG_URL as string) || "",
  gtmTagId: (process.env.REACT_APP_GTM_TAG_ID as string) || "",
  /** OpenAI Ads Measurement Pixel ID (ChatGPT ads conversion tracking). */
  openaiAdsPixelId: (process.env.REACT_APP_OPENAI_ADS_PIXEL_ID as string) || "",
  betterAuthEnabled: isBetterAuthEnabled(process.env.REACT_APP_BETTER_AUTH_ENABLED),
  /**
   * "true": visitors outside Europe (by browser time zone) get analytics by default, with ads
   * denied under Global Privacy Control. Europe keeps opt-in. Off in prod until legal review.
   */
  regionalConsent: process.env.REACT_APP_REGIONAL_CONSENT === "true",
  /**
   * Universal OAuth MCP URL (e.g. https://mcp.rivetai.com/mcp). When set, AI app
   * cards share this URL instead of creating key-bearing connector links.
   */
  mcpOAuthUrl: ((process.env.REACT_APP_MCP_OAUTH_URL as string | undefined) ?? "").trim(),
};

const hasPosthogKey = Boolean(ENVS.posthogKey?.trim());

/** Load PostHog SDK (Support + analytics transport) when a project key is configured. */
export const isPosthogSdkEnabled = hasPosthogKey;

/** Product analytics capture and session replay stay off in local dev. */
export const isPosthogEnabled = process.env.NODE_ENV !== "development" && hasPosthogKey;

export default ENVS;
